import { dateNow, faro, genShortID } from '@grafana/faro-core';
import { throttle } from '../../utils';
import { getItem, isLocalStorageAvailable, removeItem, setItem } from '../../utils/webStorage';
// TODO: make this configurable from the outside
export const SESSION_EXPIRATION_TIME = 4 * 60 * 60 * 1000; // n hrs
export const SESSION_INACTIVITY_TIME = 15 * 60 * 1000; // n minutes
const STORAGE_UPDATE_DELAY = 1 * 1000; // n seconds
export const STORAGE_KEY = '__FARO_SESSION__';
export function createUserSessionObject(sessionId) {
    const now = dateNow();
    return {
        sessionId: sessionId !== null && sessionId !== void 0 ? sessionId : genShortID(),
        lastActivity: now,
        started: now,
    };
}
export function removeUserSession() {
    removeItem(STORAGE_KEY);
}
export function storeUserSession(session) {
    setItem(STORAGE_KEY, JSON.stringify(session));
}
export function fetchUserSession() {
    const storedSession = getItem(STORAGE_KEY);
    if (storedSession) {
        return JSON.parse(storedSession);
    }
    return null;
}
export function isUserSessionValid(session) {
    if (session == null) {
        return false;
    }
    const now = dateNow();
    const lifetimeValid = now - session.started < SESSION_EXPIRATION_TIME;
    if (!lifetimeValid) {
        return false;
    }
    const inactivityPeriodValid = now - session.lastActivity < SESSION_INACTIVITY_TIME;
    return inactivityPeriodValid;
}
export function persistentUserSessionsUpdater(initialSessionId) {
    const throttledSessionUpdate = throttle(() => {
        var _a, _b, _c, _d, _e, _f;
        const sessionFromLocalStorage = fetchUserSession();
        if (isUserSessionValid(sessionFromLocalStorage)) {
            storeUserSession(Object.assign(Object.assign({}, sessionFromLocalStorage), { lastActivity: dateNow() }));
        }
        else {
            let newSession = createUserSessionObject(); // create local srorage session
            newSession = Object.assign(Object.assign({}, newSession), { sessionMeta: {
                    id: newSession.sessionId,
                    attributes: Object.assign(Object.assign({}, ((_b = (_a = faro.metas.value.session) === null || _a === void 0 ? void 0 : _a.attributes) !== null && _b !== void 0 ? _b : {})), (sessionFromLocalStorage != null ? { previousSession: sessionFromLocalStorage.sessionId } : {})),
                } });
            storeUserSession(newSession);
            (_c = faro.api) === null || _c === void 0 ? void 0 : _c.setSession(newSession.sessionMeta);
            (_e = (_d = faro.config.experimentalSessions) === null || _d === void 0 ? void 0 : _d.onSessionChange) === null || _e === void 0 ? void 0 : _e.call(_d, (_f = sessionFromLocalStorage === null || sessionFromLocalStorage === void 0 ? void 0 : sessionFromLocalStorage.sessionMeta) !== null && _f !== void 0 ? _f : null, newSession.sessionMeta);
        }
    }, STORAGE_UPDATE_DELAY);
    function init() {
        storeUserSession(createUserSessionObject(initialSessionId));
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                throttledSessionUpdate();
            }
        });
        window.addEventListener('storage', (event) => {
            var _a, _b, _c;
            if (event.key !== STORAGE_KEY) {
                return;
            }
            const newSession = JSON.parse((_a = event.newValue) !== null && _a !== void 0 ? _a : '');
            const previousSession = JSON.parse((_b = event.oldValue) !== null && _b !== void 0 ? _b : '');
            if (newSession.sessionId !== previousSession.sessionId) {
                (_c = faro.api) === null || _c === void 0 ? void 0 : _c.setSession(newSession.sessionMeta);
            }
        });
        // Users can call the setSession() method, so we need to sync this with the local storage session
        faro.metas.addListener(function syncSessionIfChangedExternally(meta) {
            var _a, _b;
            const session = meta.session;
            const sessionFromLocalStorage = fetchUserSession();
            if (session && session.id !== (sessionFromLocalStorage === null || sessionFromLocalStorage === void 0 ? void 0 : sessionFromLocalStorage.sessionId)) {
                const userSession = Object.assign(Object.assign({}, createUserSessionObject(session.id)), { sessionMeta: {
                        id: session.id,
                        attributes: Object.assign(Object.assign(Object.assign({}, ((_b = (_a = sessionFromLocalStorage === null || sessionFromLocalStorage === void 0 ? void 0 : sessionFromLocalStorage.sessionMeta) === null || _a === void 0 ? void 0 : _a.attributes) !== null && _b !== void 0 ? _b : {})), session.attributes), (sessionFromLocalStorage ? { previousSession: sessionFromLocalStorage.sessionId } : {})),
                    } });
                storeUserSession(userSession);
                faro.api.setSession(userSession.sessionMeta);
            }
        });
    }
    return {
        handleUpdate: throttledSessionUpdate,
        init,
    };
}
export function inMemoryUserSessionsUpdater(initialSessionId) {
    let inMemoryUserSession = createUserSessionObject(initialSessionId);
    function handleUpdate() {
        var _a, _b, _c, _d, _e;
        if (isUserSessionValid(inMemoryUserSession)) {
            inMemoryUserSession.lastActivity = dateNow();
        }
        else {
            inMemoryUserSession = createUserSessionObject();
            const previousSessionMeta = faro.metas.value.session;
            const newSessionMeta = {
                id: inMemoryUserSession.sessionId,
                attributes: Object.assign(Object.assign({}, ((_b = (_a = faro.metas.value.session) === null || _a === void 0 ? void 0 : _a.attributes) !== null && _b !== void 0 ? _b : {})), ((previousSessionMeta === null || previousSessionMeta === void 0 ? void 0 : previousSessionMeta.id) ? { previousSession: previousSessionMeta.id } : {})),
            };
            (_d = (_c = faro.config.experimentalSessions) === null || _c === void 0 ? void 0 : _c.onSessionChange) === null || _d === void 0 ? void 0 : _d.call(_c, previousSessionMeta !== null && previousSessionMeta !== void 0 ? previousSessionMeta : null, newSessionMeta);
            (_e = faro.api) === null || _e === void 0 ? void 0 : _e.setSession(newSessionMeta);
        }
    }
    function init() {
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                handleUpdate();
            }
        });
        // Users can call the setSession() method, so we need to sync this with the inMemoryUserSession
        faro.metas.addListener(function syncSessionIfChangedExternally(meta) {
            var _a;
            const session = meta.session;
            if (session && session.id !== (inMemoryUserSession === null || inMemoryUserSession === void 0 ? void 0 : inMemoryUserSession.sessionId)) {
                const previousSessionId = inMemoryUserSession.sessionId;
                inMemoryUserSession = createUserSessionObject(session.id);
                faro.api.setSession({
                    id: inMemoryUserSession.sessionId,
                    attributes: Object.assign(Object.assign({}, ((_a = session.attributes) !== null && _a !== void 0 ? _a : {})), { previousSession: previousSessionId }),
                });
            }
        });
    }
    return {
        handleUpdate,
        init,
    };
}
export function getSessionUpdater(initialSessionId) {
    var _a;
    if (((_a = faro.config.experimentalSessions) === null || _a === void 0 ? void 0 : _a.persistent) && isLocalStorageAvailable) {
        return persistentUserSessionsUpdater(initialSessionId);
    }
    return inMemoryUserSessionsUpdater(initialSessionId);
}
//# sourceMappingURL=sessionHandler.js.map